import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Table,
  message,
  Tag,
  Select,
  Space,
  Typography,
  Drawer,
  Descriptions,
} from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, CopyOutlined, FilterOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { startCase } from 'lodash';
import {
  listMetaFlowSubmissions,
  getMetaFlowSubmission,
  listLocalMetaFlows,
} from 'services/metaFlowService';
import CustomDrawer from 'components/shared-components/CustomDrawer';
import { useMyContext } from 'Context/MyContextProvider';

const { Text } = Typography;
const { Option } = Select;

const formatValue = (val) => {
  if (val === undefined || val === null) return '—';
  if (typeof val === 'object') {
    if (Array.isArray(val)) {
      return val.map((item) => formatValue(item)).join(', ');
    }
    return JSON.stringify(val);
  }

  const strVal = String(val).trim();
  if (!strVal) return '—';

  // Do not format email addresses or URLs
  if (strVal.includes('@') || strVal.includes('://')) {
    return strVal;
  }

  // If it contains commas, split, format each part, and join
  if (strVal.includes(',')) {
    return strVal
      .split(',')
      .map((part) => startCase(part.trim()))
      .filter(Boolean)
      .join(', ');
  }

  // Otherwise, format using startCase
  return startCase(strVal);
};

const STATUS_COLORS = {
  completed: 'success',
  in_progress: 'warning',
};


const MetaFlowResponses = () => {
  const { isMobile } = useMyContext();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flowFilter, setFlowFilter] = useState('all');
  const [flows, setFlows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    listLocalMetaFlows()
      .then((data) => setFlows(data?.data || []))
      .catch(() => setFlows([]));
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        status: statusFilter,
      };
      if (flowFilter !== 'all') {
        params.meta_flow_id = flowFilter;
      }
      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      const data = await listMetaFlowSubmissions(params);
      setRows(data?.data || []);
      setTotal(data?.meta?.total || 0);
    } catch (error) {
      console.error('Failed to load flow responses:', error);
      message.error('Failed to load flow responses');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, statusFilter, flowFilter, searchText]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await getMetaFlowSubmission(id);
      setDetail(data?.data || null);
    } catch (error) {
      message.error('Failed to load response details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Flow',
      key: 'flow',
      render: (_, record) => (
        <Text strong>{record.meta_flow_name || '—'}</Text>
      ),
    },
    {
      title: 'Number',
      dataIndex: 'wa_id',
      //width: 140,
      render: (value) => value || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      //width: 120,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>
          {(status || 'unknown').replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Last screen',
      dataIndex: 'last_screen',
      //width: 120,
      render: (value) => value || '—',
    },
    // {
    //   title: 'Answers',
    //   key: 'answers',
    //   ellipsis: true,
    //   render: (_, record) => (
    //     <Text type="secondary" style={{ fontSize: 12 }}>
    //       {formatAnswerPreview(record.answer_preview)}
    //     </Text>
    //   ),
    // },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      //width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      title: 'Action',
      key: 'actions',
      //width: 90,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => openDetail(record.id)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, marginBottom: 20 }}
      animate={{ opacity: 1, marginBottom: 0 }}
    >
      <Card>
        <Row gutter={ROW_GUTTER} justify="space-between" align="middle" className='mb-3'>
          <Col>
            <Text strong style={{ fontSize: 16 }}>MetaFlow Responses</Text>
          </Col>
          <Col>
            {isMobile ? (
              <Space size="small">
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFilterDrawerOpen(true)}
                  type={statusFilter !== 'all' || flowFilter !== 'all' || searchText ? 'primary' : 'default'}
                />
                <Button icon={<ReloadOutlined />} onClick={fetchRows} loading={loading} />
              </Space>
            ) : (
              <Space wrap size="middle">
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Search phone or answers…"
                  value={searchText}
                  onChange={(e) => {
                    setPage(1);
                    setSearchText(e.target.value);
                  }}
                  style={{ width: 220 }}
                />
                <Select
                  style={{ width: 140 }}
                  value={statusFilter}
                  onChange={(value) => {
                    setPage(1);
                    setStatusFilter(value);
                  }}
                >
                  <Option value="all">All statuses</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="in_progress">In progress</Option>
                </Select>
                <Select
                  style={{ width: 280 }}
                  value={flowFilter}
                  onChange={(value) => {
                    setPage(1);
                    setFlowFilter(value);
                  }}
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value="all">All flows</Option>
                  {flows.map((flow) => (
                    <Option key={flow.meta_flow_id} value={flow.meta_flow_id}>
                      {flow.name || flow.meta_flow_id}
                    </Option>
                  ))}
                </Select>
                <Button icon={<ReloadOutlined />} onClick={fetchRows} loading={loading}>
                  Refresh
                </Button>
              </Space>
            )}
          </Col>
        </Row>

        <CustomDrawer
          title="Filters"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Search</Text>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search phone or answers…"
                value={searchText}
                onChange={(e) => {
                  setPage(1);
                  setSearchText(e.target.value);
                }}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Status</Text>
              <Select
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={(value) => {
                  setPage(1);
                  setStatusFilter(value);
                }}
              >
                <Option value="all">All statuses</Option>
                <Option value="completed">Completed</Option>
                <Option value="in_progress">In progress</Option>
              </Select>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Flow</Text>
              <Select
                style={{ width: '100%' }}
                value={flowFilter}
                onChange={(value) => {
                  setPage(1);
                  setFlowFilter(value);
                }}
                showSearch
                optionFilterProp="children"
              >
                <Option value="all">All flows</Option>
                {flows.map((flow) => (
                  <Option key={flow.meta_flow_id} value={flow.meta_flow_id}>
                    {flow.name || flow.meta_flow_id}
                  </Option>
                ))}
              </Select>
            </div>
            <Button type="primary" block onClick={() => setFilterDrawerOpen(false)}>
              Apply Filters
            </Button>
          </Space>
        </CustomDrawer>


        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 960 }}
          pagination={{
            current: page,
            pageSize: perPage,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPerPage(nextSize);
            },
          }}
        />
      </Card >

      <CustomDrawer
        title={detail ? `Response #${detail.id}` : 'Response'}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={520}
        destroyOnClose
      >
        {detailLoading ? (
          <Text type="secondary">Loading…</Text>
        ) : detail ? (
          <>
            <Descriptions column={1} size="small" bordered className="mb-3">
              <Descriptions.Item label="Flow">{detail.meta_flow_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Number">{detail.wa_id || '—'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLORS[detail.status] || 'default'}>
                  {(detail.status || 'unknown').replace('_', ' ')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Last screen">
                {detail.last_screen || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {detail.updated_at
                  ? new Date(detail.updated_at).toLocaleString()
                  : '—'}
              </Descriptions.Item>
            </Descriptions>

            <div className="mt-3">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>Answers</Text>
                {(detail.answers || detail.response_json) &&
                  Object.keys(detail.answers || detail.response_json).length > 0 && (
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={async () => {
                        const payload = detail.answers || detail.response_json;
                        try {
                          await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
                          message.success('Answers copied as JSON');
                        } catch {
                          message.error('Failed to copy');
                        }
                      }}
                    >
                      Copy JSON
                    </Button>
                  )}
              </div>
              {(() => {
                const payload = detail.answers || detail.response_json;
                if (!payload || Object.keys(payload).length === 0) {
                  return (
                    <Text type="secondary" style={{ fontSize: 13 }}>No answers submitted</Text>
                  );
                }
                return (
                  <Descriptions column={1} size="small" bordered>
                    {Object.entries(payload).map(([key, value]) => {
                      return (
                        <Descriptions.Item key={key} label={startCase(key)}>
                          {formatValue(value)}
                        </Descriptions.Item>
                      );
                    })}
                  </Descriptions>
                );
              })()}
            </div>
          </>
        ) : (
          <Text type="secondary">No data</Text>
        )}
      </CustomDrawer>
    </motion.div >
  );
};

export default MetaFlowResponses;
